class ProductSetOffer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.offerPrice = Number.parseInt(this.dataset.offerPriceCents || "9900", 10);
    this.currency = this.dataset.offerCurrency || "USD";
    this.variantPrices = this.getVariantPrices();
    this.loadingHeading = this.querySelector("[data-set-offer-loading]");
    this.paidHeading = this.querySelector("[data-set-offer-paid]");
    this.freeHeading = this.querySelector("[data-set-offer-free]");
    this.amount = this.querySelector("[data-set-offer-amount]");
    this.link = this.querySelector("[data-set-offer-link]");
    this.form = document.getElementById(`product-form-${this.dataset.sectionId}`);
    this.variantInput = this.form?.querySelector('input[name="id"]');

    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.variantInput?.addEventListener("change", this.handleVariantChange);
    this.updateLink(this.variantInput?.value);

    if (this.dataset.currentCurrency === this.dataset.offerCurrency) {
      this.updatePrice(this.variantInput?.value);
    } else {
      this.loadOfferCurrencyPrices();
    }
  }

  disconnectedCallback() {
    this.variantInput?.removeEventListener("change", this.handleVariantChange);
  }

  getVariantPrices() {
    const data = this.querySelector("[data-set-offer-variants]");
    if (!data) return new Map();

    try {
      return new Map(JSON.parse(data.textContent).map((variant) => [String(variant.id), Number(variant.price)]));
    } catch (error) {
      return new Map();
    }
  }

  handleVariantChange(event) {
    this.updateLink(event.target.value);
    this.updatePrice(event.target.value);
  }

  async loadOfferCurrencyPrices() {
    try {
      const response = await fetch(this.dataset.productJsonUrl, {
        headers: { Accept: "application/json" }
      });

      if (!response.ok) throw new Error("Could not load offer pricing.");

      const product = await response.json();
      this.variantPrices = new Map(
        (product.variants || []).map((variant) => [String(variant.id), Number(variant.price)])
      );
      this.updatePrice(this.variantInput?.value);
    } catch (error) {
      this.showFallback();
    } finally {
      this.removeAttribute("aria-busy");
    }
  }

  updatePrice(variantId) {
    const normalizedVariantId = String(variantId || "");
    const variantPrice = this.variantPrices.get(normalizedVariantId);
    if (!Number.isFinite(variantPrice)) return;

    const remaining = Math.max(this.offerPrice - variantPrice, 0);
    const isFree = remaining === 0;

    if (this.amount) this.amount.textContent = this.formatMoney(remaining);
    if (this.loadingHeading) this.loadingHeading.hidden = true;
    if (this.paidHeading) this.paidHeading.hidden = isFree;
    if (this.freeHeading) this.freeHeading.hidden = !isFree;
  }

  updateLink(variantId) {
    if (this.link) {
      this.link.dataset.variantId = String(variantId || "");

      const url = new URL(this.dataset.builderUrl, window.location.origin);
      url.searchParams.set("setbuilder", "1");
      url.searchParams.set("first", this.dataset.productHandle);
      url.searchParams.set("variant", String(variantId || ""));
      url.hash = "buy-any-two-builder";
      this.link.href = url.href;
    }
  }

  showFallback() {
    if (this.loadingHeading) this.loadingHeading.hidden = false;
    if (this.paidHeading) this.paidHeading.hidden = true;
    if (this.freeHeading) this.freeHeading.hidden = true;
  }

  formatMoney(cents) {
    const value = cents / 100;

    return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency: this.currency,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(value);
  }
}

if (!customElements.get("product-set-offer")) {
  customElements.define("product-set-offer", ProductSetOffer);
}
